/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
/**
 * 假勤管理页面（HR 假勤申请 + 审批 + 假期余额三栏 + 假期政策设置）。
 *
 * 需求来源（2026-10-14 HR 反馈）：
 *   ① 「假期余额体系缺失」——需要「累计(accumulated) / 已用(used) / 可用(available)」三栏余额，
 *      并支持按公司政策累积（PTO 每发薪周期 1.54 小时、满 3 年翻倍；Sick/PSL 每工作 30 小时累积 1 小时；
 *      FMLA/CFRA 每人 480 工作小时、每 12 个月重置）；
 *   ② 「假期类型不全」——补齐 PTO / PSL / LWOP / Jury Duty / Bereavement / FMLA-CFRA；
 *   ③ 需要「登记休假」入口（沿用原有申请对话框）。
 *
 * 实现说明：
 *   - 余额由后端 /hrms/leaves/balances 按当年实时计算（累计=按政策推算，已用=当年已批准小时，可用=累计−已用）；
 *   - 政策由 /hrms/leaves/policy 读写，HR 可在「假期政策设置」里调整累积规则；
 *   - 页面分「申请记录」与「假期余额」两个视图，用标签切换，避免信息挤在一张表里。
 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2, Plus, Check, X, Settings2, Wallet, ClipboardList } from "lucide-react";
import { LEAVE_TYPES, LEAVE_TYPES_EN, inp, useHrmsManage } from "./hrmsShared";
import { fmtD } from "../../lib/date";

const LV_STATUS = {
  pending: { label: "待审批", en: "Pending", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "已通过", en: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "已拒绝", en: "Rejected", cls: "bg-red-100 text-red-600" },
};
// 余额三栏展示的默认类型顺序（含 US 合规假期类型）
const BALANCE_ORDER = ["pto", "psl", "fmla", "annual", "sick", "jury", "bereavement"];
// 累积规则模式 → 中文标签
const MODE_LABEL = {
  perPayPeriod: "按发薪周期累积", perHoursWorked: "按工作小时累积",
  annualHours: "年度固定额度(小时)", annualDays: "年度固定额度(天)", none: "不计额度（记录型）",
};
// 累积参数字段 → 中文标签
const PARAM_LABEL = {
  hoursPerPeriod: "每周期小时", tenureDoubleYears: "满此工龄翻倍(年)", hoursPer: "每工作(小时)",
  hoursEarned: "累积(小时)", hours: "年度小时", resetMonths: "重置周期(月)", days: "年度天数",
};

export default function HRMSLeaves({ embedded = false, autoOpen = false, onAutoOpened }) {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const LT = zh ? LEAVE_TYPES : LEAVE_TYPES_EN;
  const canManage = useHrmsManage(user);
  const canApprove = canManage;
  const [list, setList] = useState(null);
  const [bal, setBal] = useState(null);
  const [view, setView] = useState("list"); // list | balance
  const [balType, setBalType] = useState("pto");
  const [dlg, setDlg] = useState(null);
  const [polDlg, setPolDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api.get("/hrms/leaves").then(({ data }) => setList(data)).catch(() => setList([]));
    api.get("/hrms/leaves/balances").then(({ data }) => setBal(data)).catch(() => setBal(null));
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  /* 外部自动打开请假弹窗（HRMS 走查一-6）。
     场景：考勤页「快捷打卡」条右侧新增的蓝色「申请请假」按钮，点它希望**就地**弹出请假表单，
     而不是先切页签、再让用户自己找到按钮再点一次。
     为什么用 prop 而不是在考勤页复制一份弹窗：请假表单含类型 / 起止日期 / 天数 / 事由 +
     校验 + POST /hrms/leaves，复制一份必然两处口径漂移（改了一处忘另一处）。
     一次性语义：打开后立刻回调 onAutoOpened 让父组件把标志位清掉，
     否则用户手动关掉弹窗后，父组件任何一次 re-render 都会把它again弹出来。 */
  useEffect(() => {
    if (!autoOpen) return;
    setDlg({ type: "annual", startDate: "", endDate: "", days: 1, reason: "" });
    onAutoOpened?.();
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!dlg.startDate || !dlg.endDate) return toast.error(T("请填写起止日期", "Start and end dates required"));
    setBusy(true);
    try { await api.post("/hrms/leaves", dlg); toast.success(T("申请已提交", "Request submitted")); setDlg(null); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("提交失败", "Submit failed")); }
    setBusy(false);
  };
  const approve = async (lv, ok) => {
    try { await api.post(`/hrms/leaves/${lv.id}/approve`, { approve: ok }); toast.success(ok ? T("已通过", "Approved") : T("已拒绝", "Rejected")); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("操作失败", "Action failed")); }
  };
  const savePolicy = async () => {
    setBusy(true);
    try { await api.put("/hrms/leaves/policy", polDlg); toast.success(T("假期政策已保存", "Leave policy saved")); setPolDlg(null); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const openPolicy = async () => {
    try { const { data } = await api.get("/hrms/leaves/policy"); setPolDlg(JSON.parse(JSON.stringify(data.policy))); }
    catch (e) { toast.error(e?.response?.data?.detail || T("加载失败", "Load failed")); }
  };

  const balTypes = bal?.balanceTypes || BALANCE_ORDER;
  const labels = bal?.labels || {};
  const rows = bal?.rows || [];

  return (
    <div className="space-y-4" data-testid="hrms-leaves-page">
      <div className="flex flex-wrap items-center gap-3">
        {!embedded && (
          <div>
            <h1 className="text-xl font-extrabold text-slate-800">{T("假勤管理", "Leave Management")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{T("请假申请与审批 · 假期余额（累计/已用/可用）· 合规假期类型", "Leave requests & approvals · balances (accrued/used/available) · compliance leave types")}</p>
          </div>
        )}
        {canManage && (
          <button data-testid="hrms-lv-policy-btn" onClick={openPolicy}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold px-3 py-1.5">
            <Settings2 className="h-3.5 w-3.5" />{T("假期政策设置", "Policy Settings")}
          </button>
        )}
        <button data-testid="hrms-lv-create-btn" onClick={() => setDlg({ type: "annual", startDate: "", endDate: "", days: 1, reason: "" })}
          className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 ${canManage ? "" : "ml-auto"} inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-4 py-2`}>
          <Plus className="h-3.5 w-3.5" />{T("申请请假", "Request Leave")}
        </button>
      </div>

      <div className="flex-wrap md:flex-nowrap flex items-center gap-1 text-xs">
        <button data-testid="hrms-lv-tab-list" onClick={() => setView("list")}
          className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-semibold border ${view === "list" ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
          <ClipboardList className="h-3.5 w-3.5" />{T("申请记录", "Requests")}
        </button>
        <button data-testid="hrms-lv-tab-balance" onClick={() => setView("balance")}
          className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-semibold border ${view === "balance" ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
          <Wallet className="h-3.5 w-3.5" />{T("假期余额", "Balances")}
        </button>
      </div>

      {view === "list" ? (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">{T("员工", "Employee")}</th><th className="px-3 py-2">{T("类型", "Type")}</th><th className="px-3 py-2">{T("起止日期", "Dates")}</th>
              <th className="px-3 py-2">{T("天数", "Days")}</th><th className="px-3 py-2">{T("事由", "Reason")}</th><th className="px-3 py-2">{T("状态", "Status")}</th>
              {canApprove && <th className="px-3 py-2 text-right">{T("审批", "Approve")}</th>}
            </tr></thead>
            <tbody>
              {list === null ? <tr><td colSpan={7} className="text-center py-8 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                list.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-300">{T("暂无请假记录", "No leave records")}</td></tr> :
                list.map((lv) => (
                  <tr key={lv.id} className="border-b border-slate-50" data-testid={`hrms-lv-row-${lv.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-700">{lv.employeeName}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{LT[lv.type] || lv.type}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{fmtD(lv.startDate)} ~ {fmtD(lv.endDate)}</td>
                    <td className="px-3 py-2">{lv.days}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs max-w-[200px] truncate">{lv.reason || "-"}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${LV_STATUS[lv.status]?.cls}`}>{zh ? LV_STATUS[lv.status]?.label : LV_STATUS[lv.status]?.en}</span>{lv.approvedBy ? <span className="text-[11px] text-slate-400 ml-1">{lv.approvedBy}</span> : null}</td>
                    {canApprove && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {lv.status === "pending" && (
                          <>
                            <button data-testid={`hrms-lv-approve-${lv.id}`} onClick={() => approve(lv, true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-emerald-500 hover:text-emerald-700 mr-2"><Check className="h-4 w-4" /></button>
                            <button data-testid={`hrms-lv-reject-${lv.id}`} onClick={() => approve(lv, false)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
      ) : (
        <div className="space-y-3" data-testid="hrms-lv-balance-section">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">{T("假期类型", "Leave type")}</span>
            <div className="flex flex-wrap gap-1">
              {balTypes.map((k) => (
                <button key={k} data-testid={`hrms-lv-bal-type-${k}`} onClick={() => setBalType(k)}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full px-3 py-1 border font-semibold ${balType === k ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  {labels[k] || (zh ? LEAVE_TYPES[k] : LEAVE_TYPES_EN[k]) || k}
                </button>
              ))}
            </div>
            {bal && <span className="ml-auto text-[11px] text-slate-400">{T(`${bal.year} 年度 · 单位：小时`, `Year ${bal.year} · unit: hours`)}</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2">{T("员工", "Employee")}</th>
                <th className="px-3 py-2">{T("工龄", "Tenure")}</th>
                <th className="px-3 py-2 text-right">{T("累计（Accrued）", "Accrued")}</th>
                <th className="px-3 py-2 text-right">{T("已用（Used）", "Used")}</th>
                <th className="px-3 py-2 text-right">{T("可用（Available）", "Available")}</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-slate-300">{T("暂无数据", "No data")}</td></tr> :
                  rows.map((r) => {
                    const t = (r.types || {})[balType] || {};
                    return (
                      <tr key={r.employeeId} className="border-b border-slate-50" data-testid={`hrms-lv-bal-row-${r.employeeId}`}>
                        <td className="px-3 py-2 font-semibold text-slate-700">{r.name}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.tenureYears} {T("年", "y")}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{t.accrued ?? 0}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{t.used ?? 0}</td>
                        <td className={`px-3 py-2 text-right font-bold ${(t.available ?? 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.available ?? 0}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </ResponsiveTable>
          </div>
          <p className="text-[11px] text-slate-400">{T("累计 = 按假期政策自动推算（PTO 每发薪周期 1.54h、满 3 年翻倍；PSL 每工作 30h 累积 1h；FMLA/CFRA 480h、PDL 320h）；已用 = 已批准休假小时；可用 = 累计 − 已用。FMLA/CFRA 与 PDL 按「滚动 12 个月」口径重置，其余类型按当年统计。", "Accrued = auto from leave policy; Used = approved leave hours; Available = Accrued − Used. FMLA/CFRA and PDL reset on a rolling 12-month window; other types use the calendar year.")}</p>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("申请请假", "Request Leave")}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3 text-sm">
              <label className="block">{T("类型", "Type")}<select data-testid="hrms-lv-type" className={inp} value={dlg.type} onChange={(e) => setDlg((d) => ({ ...d, type: e.target.value }))}>{Object.keys(LEAVE_TYPES).map((k) => <option key={k} value={k}>{LT[k]}</option>)}</select></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label>{T("开始日期", "Start date")} *<input data-testid="hrms-lv-start" type="date" className={inp} value={dlg.startDate} onChange={(e) => setDlg((d) => ({ ...d, startDate: e.target.value }))} /></label>
                <label>{T("结束日期", "End date")} *<input data-testid="hrms-lv-end" type="date" className={inp} value={dlg.endDate} onChange={(e) => setDlg((d) => ({ ...d, endDate: e.target.value }))} /></label>
              </div>
              <label className="block">{T("天数", "Days")}<input data-testid="hrms-lv-days" type="number" min="0.5" step="0.5" className={inp} value={dlg.days} onChange={(e) => setDlg((d) => ({ ...d, days: e.target.value }))} /></label>
              <label className="block">{T("事由", "Reason")}<input data-testid="hrms-lv-reason" className={inp} value={dlg.reason} onChange={(e) => setDlg((d) => ({ ...d, reason: e.target.value }))} /></label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-lv-submit-btn" disabled={busy} onClick={create} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("提交申请", "Submit")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!polDlg} onOpenChange={(o) => !o && setPolDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{T("假期政策设置", "Leave Policy Settings")}</DialogTitle></DialogHeader>
          {polDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-lv-policy-dlg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">{T("每年发薪周期数", "Pay periods / year")}<input data-testid="hrms-lv-pol-periods" className={inp} value={polDlg.payPeriodsPerYear} onChange={(e) => setPolDlg((d) => ({ ...d, payPeriodsPerYear: e.target.value }))} /></label>
                <label className="text-xs text-slate-500">{T("每工作日小时数", "Hours per day")}<input data-testid="hrms-lv-pol-hpd" className={inp} value={polDlg.hoursPerDay} onChange={(e) => setPolDlg((d) => ({ ...d, hoursPerDay: e.target.value }))} /></label>
              </div>
              {(balTypes.length ? balTypes : Object.keys(polDlg.types || {})).map((k) => {
                const tp = (polDlg.types || {})[k] || {};
                const acc = tp.accrual || {};
                return (
                  <div key={k} className="rounded-lg border border-slate-200 p-2.5">
                    <p className="text-xs font-bold text-slate-600 mb-1.5">{zh ? LEAVE_TYPES[k] : LEAVE_TYPES_EN[k]}<span className="ml-2 font-normal text-slate-400">{MODE_LABEL[acc.mode] || acc.mode || "—"}</span></p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.keys(acc).filter((pk) => pk !== "mode").map((pk) => (
                        <label key={pk} className="text-[11px] text-slate-500">{PARAM_LABEL[pk] || pk}
                          <input data-testid={`hrms-lv-pol-${k}-${pk}`} className={inp} value={acc[pk]}
                            onChange={(e) => setPolDlg((d) => {
                              const types = { ...(d.types || {}) };
                              types[k] = { ...(types[k] || {}), accrual: { ...acc, [pk]: e.target.value } };
                              return { ...d, types };
                            })} />
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setPolDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-lv-pol-save" disabled={busy} onClick={savePolicy} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存政策", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
